import { useCallback, useState } from 'react';
import { usePathname } from 'expo-router';
import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import {
  getAppNavigationContainerRef,
  useActiveRouteName,
  useNavigateBack,
} from '../../navigation';

// Closes the bag if it's open.
export const useCloseBag = () => {
  const isBagOpen = useIsBagOpen();
  const { handleCloseBag } = useBagNavigation();

  return useCallback(() => {
    if (isBagOpen) handleCloseBag();
  }, [handleCloseBag, isBagOpen]);
};

// Returns whether the bag is opened by using the active route name.
export const useIsBagOpen = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const activeRouteName = useActiveRouteName();
  const pathname = useSafePathname();

  if (isExpoRouterEnabled) {
    return pathname === '/bag';
  }

  return activeRouteName === 'Bag';
};

// Returns whether the bag is opened by using the nav ref (works outside of navigation).
// It can either be { name: 'Modal', params: { screen: 'Bag'  } } or { name: 'Bag' }.
export const useIsBagOpenFromReference = () => {
  const [isBagOpen, setIsBagOpen] = useState(false);

  getAppNavigationContainerRef()?.current?.addListener('state', () => {
    const route = getAppNavigationContainerRef()?.current?.getCurrentRoute();
    const params = route?.params as Readonly<{ screen: string }>;

    setIsBagOpen(route?.name === 'Bag' || params?.screen === 'Bag');
  });

  return isBagOpen;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function useSafePathname() {
  try {
    return usePathname();
  } catch {
    return null;
  }
}

const useBagNavigation = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();
  const navigation = useSafeNavigation();

  // This just opens the bag screen on the modal stack.
  const handleOpenBag = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/bag');
      return;
    }

    navigation?.navigate('Modal', { screen: 'Bag' });
  }, [isExpoRouterEnabled, router, navigation]);

  // This will go back on the parent (dismiss the modal stack),
  // It will go back in the modal stack if needed.
  const handleCloseBag = useNavigateBack();

  return {
    handleOpenBag,
    handleCloseBag,
  };
};
